import React from 'react';
import produce from 'immer';
import cx from 'classnames';

import ProductDetailPanel from '../../ProductDetailPanel';
import BaseForm from '../../../../../components/form/BaseForm';
import SelectForm from '../../../../../components/form/SelectForm';
import InputForm from '../../../../../components/form/InputForm';
import Tag from '../../../../../components/tag/Tag/Tag';
import useFormState from '../../../../../hooks/product/useFormState';
import usePropertyTag from './usePropertyTag';
import { formRequiredRules } from '../../../../../lib/formRules';
import { compareProductVersion } from '../../../../../lib/utils';
import {
  MAXIMUM_NUMBER_OF_PRODUCT_CONCEPT_TAG,
  MAXIMUM_NUMBER_OF_SEASON_STATUS_TAG,
  PRODUCT_DETAIL_PAGE_PANEL_NAME,
} from '../../../../../constants/service/app/productConstants';
import './ProductTagInfo.scss';

const TagInput = (props) => {
  const {
    className,
    isEditable,
    form,
    name,
    label,
    required,
    max,
    list = [],
    initialValue = [],
    onSelect,
    onDeselect,
    subContent,
  } = props;

  const onCloseTag = (value) => {
    onDeselect(value);

    // 태그 닫기 시 form 데이터에도 제거
    const target = form.getFieldValue(name);
    form.setFieldsValue({
      [name]: target.filter((id) => id !== value.id),
    });
  };

  return (
    <BaseForm
      className={cx('tag-input', className)}
      label={label}
      required={required}
      content={
        <>
          <div className={'tag-wrapper'}>
            {initialValue.map((tag) => (
              <Tag
                key={`${tag.id}_${tag.name}`}
                text={tag.name}
                // onClick={() => tag.inActive && checkTagName(tag.name)}
                onClose={() => onCloseTag({ id: tag.id, name })}
                closable={isEditable}
              />
            ))}
          </div>
          <SelectForm
            isEditable={isEditable}
            className="tag-select"
            form={form}
            name={name}
            isTable={true}
            rules={[
              formRequiredRules(required, null, false),
              {
                validator: (rule, value, cb) => {
                  if (max && value.length > max) {
                    cb(`해당 태그 분류에서는 태그를 최대 ${max}개까지만 등록할 수 있습니다.`);
                  } else cb();
                },
              },
            ]}
            showSearch
            optionFilterProp="children"
            list={list}
            mode="multiple"
            initialValue={initialValue.map((item) => item.id)}
            onSelect={(id) => onSelect({ id, name })}
            onDeselect={(id) => onDeselect({ id, name })}
            subContent={subContent}
          />
        </>
      }
    />
  );
};

const ProductTagInfo = (props) => {
  const { isEditable, form, initialValue, productTagInfo, isCompare, comparisonData, ...res } = props;
  const [tagInfo, setTagInfo] = useFormState(initialValue, { form, key: 'productTagInfo' });
  const propertyTag = usePropertyTag(form);

  const addTagInfo = ({ id, name }) => {
    const target = productTagInfo[name].find((item) => item.id === id);

    setTagInfo((prev) => {
      return produce(prev, (draft) => {
        draft[name].list.push({ id: target.id, name: target.name });
      });
    });
  };
  const deleteTagInfo = ({ id, name }) => {
    setTagInfo((prev) => {
      return produce(prev, (draft) => {
        draft[name].list = prev[name].list.filter((item) => item.id !== id);
      });
    });
  };

  return (
    <ProductDetailPanel
      name={PRODUCT_DETAIL_PAGE_PANEL_NAME.PRO_TAG_INFO}
      className="product-tag-info"
      title="상품 태그 정보"
      isCompare={isCompare}
      {...res}
    >
      <div className="panel-body">
        <TagInput
          className={cx(
            'conceptTag',
            isCompare && compareProductVersion(tagInfo.conceptTag, comparisonData.conceptTag),
          )}
          isEditable={isEditable}
          form={form}
          name="conceptTag"
          label="컨셉 태그"
          list={productTagInfo?.conceptTag}
          initialValue={tagInfo.conceptTag.list}
          max={MAXIMUM_NUMBER_OF_PRODUCT_CONCEPT_TAG}
          subContent={
            <div className="sub-info">
              {`*상품 컨셉은 최대 ${MAXIMUM_NUMBER_OF_PRODUCT_CONCEPT_TAG}개까지 선택이 가능합니다.`}
            </div>
          }
          onSelect={addTagInfo}
          onDeselect={deleteTagInfo}
        />
        <TagInput
          className={cx(
            'seasonSituationTag',
            isCompare && compareProductVersion(tagInfo.seasonSituationTag, comparisonData.seasonSituationTag),
          )}
          isEditable={isEditable}
          form={form}
          name="seasonSituationTag"
          label="시즌/상황 태그"
          list={productTagInfo?.seasonSituationTag}
          initialValue={tagInfo.seasonSituationTag.list}
          max={MAXIMUM_NUMBER_OF_SEASON_STATUS_TAG}
          subContent={
            <div className="sub-info">
              {`*시즌/상황은 최대 ${MAXIMUM_NUMBER_OF_SEASON_STATUS_TAG}개까지 선택이 가능합니다.`}
            </div>
          }
          onSelect={addTagInfo}
          onDeselect={deleteTagInfo}
        />
        <TagInput
          isEditable={false}
          form={form}
          name="propertyTag"
          label="속성 태그"
          list={[]}
          initialValue={propertyTag}
          subContent={
            <div className="sub-info">
              {`*입력하신 상품 속성 정보 기반의 예상 태그 입니다. 속성과 동일한 태그가 태그 리스트에 있는 경우에만 태그가 최종 저장됩니다.`}
            </div>
          }
        />
      </div>
      <div className="hidden-form">
        <InputForm form={form} type="hidden" name="productTagInfo" initialValue={tagInfo} />
      </div>
    </ProductDetailPanel>
  );
};

export default ProductTagInfo;
